import type { GroupPublic } from '@repo/types/groups';
import type { UserPublic } from '@repo/types/users';

import { useGetGroupsInfinite } from '@repo/api/group/useGetGroupsInfinite';
import { useGetUsersInfinite } from '@repo/api/user/useGetUsersInfinite';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { getUserLvl } from '@repo/lib/user-lvl/getUserLvl';
import { User, Users } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import {
  SelectList,
  SelectListElement,
  SelectListElementContent,
  SelectListElementDescription,
  SelectListElementSkeleton,
  SelectListElementTitle,
  SelectListElementTrigger,
  SelectListInfiniteQueryStates,
} from '@/components/ui/select-list';
import { getAvatar } from '@/utils/getAvatar';

interface UsersSelectTabProps<T> {
  searchValue: string;
  selected: T[];
  setSelected: (item: T[]) => void;
  enabled: boolean;
}

export function UsersSelectTab(usersSelectTabProps: UsersSelectTabProps<UserPublic>) {
  const { searchValue, selected, setSelected, enabled } = usersSelectTabProps;
  const { errorMessage: usersErrorMessage, ...getUsersInfinite } = useGetUsersInfinite(searchValue, enabled);

  const { data, isEmpty, noResults } = useInfiniteQueryResult(getUsersInfinite.data, searchValue);

  return (
    <SelectList
      type={'multiple'}
      selected={selected.map((u) => String(u.id))}
      setSelected={(ids) => {
        const selectedIds = new Set(ids.map(Number));
        const updatedUsers = [...selected.filter((u) => selectedIds.has(u.id)), ...data.filter((u) => selectedIds.has(u.id) && !selected.some((s) => s.id === u.id))];
        setSelected(updatedUsers);
      }}
    >
      <SelectListInfiniteQueryStates
        query={getUsersInfinite}
        isEmpty={isEmpty}
        noResults={noResults}
        isEmptyMessage={'Wyszukaj użytkownika'}
        noResultMessage={'Nie znaleziono użytkownika'}
        errorMessage={usersErrorMessage}
      />

      {data.map((entity) => (
        <SelectListElement key={entity.id} id={String(entity.id)}>
          <Avatar size={'sm'}>
            <AvatarImage src={getAvatar(entity.avatarURL ?? undefined)} alt={entity.name} />
            <AvatarFallback>
              <User />
            </AvatarFallback>
          </Avatar>
          <SelectListElementContent>
            <SelectListElementTitle>
              {entity.name} {entity.surname}
            </SelectListElementTitle>
            <SelectListElementDescription>{getUserLvl(entity.lvl)}</SelectListElementDescription>
          </SelectListElementContent>

          <SelectListElementTrigger>
            {({ selected, toggleElement }) => (
              <Checkbox
                checked={selected}
                onCheckedChange={() => {
                  toggleElement();
                }}
                className="after:absolute after:inset-0"
              />
            )}
          </SelectListElementTrigger>
        </SelectListElement>
      ))}
      <InfiniteScroll hasNextPage={getUsersInfinite.hasNextPage} isLoading={getUsersInfinite.isLoading} isFetching={getUsersInfinite.isFetching} next={getUsersInfinite.fetchNextPage} threshold={0.65}>
        {getUsersInfinite.hasNextPage && <SelectListElementSkeleton />}
      </InfiniteScroll>
    </SelectList>
  );
}

export function GroupsSelectTab(groupsSelectTabProps: UsersSelectTabProps<GroupPublic>) {
  const { searchValue, selected, setSelected, enabled } = groupsSelectTabProps;
  const { errorMessage: groupsErrorMessage, ...getGroupsInfinite } = useGetGroupsInfinite(searchValue, enabled);

  const { data: groups, isEmpty, noResults } = useInfiniteQueryResult(getGroupsInfinite.data, searchValue);

  return (
    <SelectList
      type={'multiple'}
      selected={selected.map((g) => String(g.id))}
      setSelected={(ids) => {
        const selectedIds = new Set(ids.map(Number));
        const updatedGroups = [...selected.filter((g) => selectedIds.has(g.id)), ...groups.filter((g) => selectedIds.has(g.id) && !selected.some((s) => s.id === g.id))];
        setSelected(updatedGroups);
      }}
    >
      <SelectListInfiniteQueryStates
        query={getGroupsInfinite}
        isEmpty={isEmpty}
        noResults={noResults}
        isEmptyMessage={'Wyszukaj grupę'}
        noResultMessage={'Nie znaleziono grupy'}
        errorMessage={groupsErrorMessage}
      />

      {groups.map((entity) => (
        <SelectListElement key={entity.id} id={String(entity.id)}>
          <Avatar size={'sm'}>
            <AvatarImage src={getAvatar(entity.avatarURL ?? undefined)} alt={entity.name} />
            <AvatarFallback>
              <Users />
            </AvatarFallback>
          </Avatar>
          <SelectListElementContent>
            <SelectListElementTitle>{entity.name}</SelectListElementTitle>
            <SelectListElementDescription>Grupa</SelectListElementDescription>
          </SelectListElementContent>

          <SelectListElementTrigger>
            {({ selected, toggleElement }) => (
              <Checkbox
                checked={selected}
                onCheckedChange={() => {
                  toggleElement();
                }}
                className="after:absolute after:inset-0"
              />
            )}
          </SelectListElementTrigger>
        </SelectListElement>
      ))}
      <InfiniteScroll
        hasNextPage={getGroupsInfinite.hasNextPage}
        isLoading={getGroupsInfinite.isLoading}
        isFetching={getGroupsInfinite.isFetching}
        next={getGroupsInfinite.fetchNextPage}
        threshold={0.65}
      >
        {getGroupsInfinite.hasNextPage && <SelectListElementSkeleton />}
      </InfiniteScroll>
    </SelectList>
  );
}
